import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BUILD_NETWORK, Network } from "../utils/network";

interface userState {
  network: Network | undefined;
  // The Firebase uid this tab's stored identity belongs to. Written ONLY by a
  // sign-in performed in this tab, never by a cross-tab sync — that asymmetry is
  // the whole point. Firebase's user is origin-shared, so when another tab signs
  // someone else in, this tab's Firebase user changes while authUid does not, and
  // the mismatch is detectable. Without it, `userType` says nothing about WHICH
  // account.
  authUid: string | undefined;
  deployedBalance: number | undefined;
  treasuryId: string | undefined;
  userType: string | undefined;
  /** Raw preference; the effective token is derived in features/tokens/useSettlementToken. */
  settlementTokenAddress: string | undefined;
}

const initialState: userState = {
  network: BUILD_NETWORK,
  authUid: undefined,
  deployedBalance: undefined,
  treasuryId: undefined,
  userType: undefined,
  settlementTokenAddress: undefined,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setAuthUid: (state, action: PayloadAction<string | undefined>) => {
      state.authUid = action.payload;
    },
    setDeployedBalance: (state, action: PayloadAction<number>) => {
      state.deployedBalance = action.payload;
    },
    setTreasuryId: (state, action: PayloadAction<string>) => {
      state.treasuryId = action.payload;
    },
    // Payload allows undefined so callers can clear the value back to
    // initialState. The state field was already `string | undefined`; only the
    // action was narrower. Nothing in this app clears it yet — the merchants and
    // users apps do, on the login error path — so this is for consistency across
    // the three slices rather than to satisfy a current caller.
    setUserType: (state, action: PayloadAction<string | undefined>) => {
      state.userType = action.payload;
    },
    /**
     * Forget the signed-in session. treasuryId and userType are redux-persist whitelisted, so
     * without it a sign-out leaves them in localStorage for the next person to sign in on this
     * browser. `authUid` deliberately survives: the cross-tab identity guards read it, and a null
     * Firebase user already reports "signed-out" on its own.
     */
    resetUserSession: (state) => {
      state.deployedBalance = undefined;
      state.treasuryId = undefined;
      state.userType = undefined;
    },
    setSettlementTokenAddress: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.settlementTokenAddress = action.payload;
    },
  },
});

export const {
  setAuthUid,
  setTreasuryId,
  setDeployedBalance,
  setUserType,
  resetUserSession,
  setSettlementTokenAddress,
} = userSlice.actions;
export default userSlice.reducer;
